import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import { MedicusLogo } from '@components/MedicusLogo';
import { TitularStep } from '@components/onboarding/TitularStep';
import { FamiliaStep } from '@components/onboarding/FamiliaStep';
import { DdjjStep } from '@components/onboarding/DdjjStep';
import { PlanSuperadorStep } from '@components/onboarding/PlanSuperadorStep';
import { PagoStep } from '@components/onboarding/PagoStep';
import { OtpGate } from '@components/onboarding/OtpGate';
import {
  finalizarLegajo,
  getLegajoByToken,
  patchFamiliar,
  patchTitular,
  type IntegranteDto,
} from '@lib/services/portal.service';

type StepKey = 'titular' | 'familia' | 'ddjj' | 'plan' | 'pago' | 'final';

const STEPS: Array<{ key: StepKey; label: string; titulo: string }> = [
  { key: 'titular', label: 'Tus datos', titulo: 'Validá tus datos' },
  { key: 'familia', label: 'Tu familia', titulo: 'Tu grupo familiar' },
  { key: 'ddjj', label: 'Información médica', titulo: 'Información médica' },
  { key: 'plan', label: 'Plan', titulo: '' },
  { key: 'pago', label: 'Pago', titulo: '' },
  { key: 'final', label: 'Cierre', titulo: '' },
];

function extractServerError(err: unknown, fallback: string): string {
  const e = err as { response?: { data?: { error?: { message?: string } } } };
  return e.response?.data?.error?.message ?? fallback;
}

// Estados de early-return del wizard. Extraídos a sub-componentes para bajar
// la cognitive-complexity de `ActivatePage` (Sonar) — cada uno encapsula su
// propio layout + ternarios sin contaminar la métrica del componente padre.

function LoadingState() {
  return (
    <div className="onboarding">
      <p className="text-element-mid" style={{ textAlign: 'center', marginTop: 80 }}>
        Cargando tu legajo…
      </p>
    </div>
  );
}

function ErrorState({ message, onVolver }: { message: string; onVolver: () => void }) {
  return (
    <div className="onboarding">
      <header className="onboarding__topbar">
        <MedicusLogo width={110} />
      </header>
      <main className="onboarding__main">
        <div className="landing__card" style={{ maxWidth: 460, margin: '40px auto' }}>
          <h2
            className="heading-5"
            style={{ marginBottom: 8, color: 'var(--color-error-dark)' }}
          >
            Link inválido o vencido
          </h2>
          <p className="text-element-mid" style={{ fontSize: 14, marginBottom: 16 }}>
            {message}
          </p>
          <button className="btn btn-primary" onClick={onVolver}>
            <span className="btn-font-style">Volver al inicio</span>
          </button>
        </div>
      </main>
    </div>
  );
}

function BlockedState({
  motivo,
  mensaje,
  onVolver,
}: {
  motivo: string;
  mensaje?: string;
  onVolver: () => void;
}) {
  const esRechazado = motivo === 'rechazado';
  const esBloqueado = motivo === 'bloqueado';
  let titulo = 'Tu legajo está en revisión';
  let color = 'var(--color-primary-darker)';
  let bajada = 'Medicus está revisando tu legajo. Te avisaremos cuando esté habilitado para continuar.';
  if (esRechazado) {
    titulo = 'Tu legajo fue rechazado';
    color = 'var(--color-error-dark)';
    bajada =
      'Medicus revisó tu legajo y lo rechazó. Comunicate con el área de RRHH de tu organización para que actualicen tus datos.';
  } else if (esBloqueado) {
    titulo = 'Legajo no disponible';
    bajada = mensaje ?? 'Este legajo no se encuentra disponible para seguir con la validación de datos.';
  }
  // Para bloqueado el mensaje ya está en la bajada → no repetirlo en el box "Motivo".
  const mostrarMotivo = !!mensaje && !esBloqueado;
  return (
    <div className="onboarding">
      <header className="onboarding__topbar">
        <MedicusLogo width={110} />
      </header>
      <main className="onboarding__main">
        <div className="landing__card" style={{ maxWidth: 460, margin: '40px auto' }}>
          <h2 className="heading-5" style={{ marginBottom: 8, color }}>
            {titulo}
          </h2>
          <p className="text-element-mid" style={{ fontSize: 14, marginBottom: 12 }}>
            {bajada}
          </p>
          {mostrarMotivo && (
            <p
              className="text-element-mid"
              style={{
                fontSize: 13,
                marginBottom: 16,
                padding: '8px 10px',
                background: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: 8,
              }}
            >
              <strong>Motivo:</strong> {mensaje}
            </p>
          )}
          <button className="btn btn-primary" onClick={onVolver}>
            <span className="btn-font-style">Volver al inicio</span>
          </button>
        </div>
      </main>
    </div>
  );
}

function FinalCard({
  tieneObservaciones,
  cantObservaciones,
}: {
  tieneObservaciones: boolean;
  cantObservaciones: number;
}) {
  const label =
    cantObservaciones === 1 ? '1 dato como incorrecto' : `${cantObservaciones} datos como incorrectos`;
  return (
    <div
      className="landing__card"
      style={{
        maxWidth: 540,
        margin: '0 auto',
        textAlign: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 36,
        paddingBottom: 32,
        borderTop: '4px solid var(--color-success)',
        background:
          'radial-gradient(120% 80% at 50% -10%, var(--color-success-lighter-ext), transparent 60%), var(--color-background)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          width: 72,
          height: 72,
          borderRadius: '50%',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--color-success-light)',
          color: 'var(--color-success-dark)',
          boxShadow: '0 0 0 8px var(--color-success-lighter-ext)',
          marginBottom: 18,
        }}
      >
        <CheckCircle2 size={40} strokeWidth={2.2} />
      </span>

      <h2 className="heading-3" style={{ marginBottom: 12 }}>
        ¡Listo!
      </h2>

      {!tieneObservaciones ? (
        <p className="text-element-mid" style={{ fontSize: 15 }}>
          Tus datos serán procesados a la brevedad. Te avisaremos cómo
          continuar mediante un correo.
        </p>
      ) : (
        <>
          <p className="text-element-mid" style={{ fontSize: 15, marginBottom: 12 }}>
            Recibimos tus datos y la declaración jurada de salud.
          </p>
          <p style={{ fontSize: 14, marginBottom: 12, color: 'var(--color-element-high)' }}>
            Reportaste {label}.{' '}
            <strong>
              Tus datos serán revisados por Medicus antes de procesar tu alta.
            </strong>
          </p>
          <p className="text-element-mid" style={{ fontSize: 13 }}>
            Te avisaremos por mail cuando esté todo verificado y se emita tu credencial.
          </p>
        </>
      )}

      <p
        style={{
          marginTop: 22,
          paddingTop: 18,
          borderTop: '1px solid var(--color-outline)',
          width: '100%',
          fontSize: 13,
          color: 'var(--color-element-mid)',
        }}
      >
        Para más información ingresá a{' '}
        <a
          href="https://medicus.com.ar/ministeriodeseguridad"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--color-primary-darker)', fontWeight: 600, textDecoration: 'underline' }}
        >
          medicus.com.ar/ministeriodeseguridad
        </a>
      </p>
    </div>
  );
}

export function ActivatePage() {
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<StepKey>('titular');
  const [globalError, setGlobalError] = useState<string | null>(null);

  // Cuando cambia el step (continuar o volver), llevamos la vista al tope.
  // Sin esto, el usuario aterriza a mitad del nuevo step si el anterior era
  // largo (DDJJ, plan superador) — se confunde con qué hay arriba.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  const legajoQ = useQuery({
    queryKey: ['onboarding', token],
    queryFn: () => getLegajoByToken(token),
    enabled: !!token,
    retry: false,
  });

  const titularMut = useMutation({
    mutationFn: (patch: Partial<IntegranteDto>) => patchTitular(token, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboarding', token] });
      setStep('familia');
    },
    onError: (err: unknown) =>
      setGlobalError(extractServerError(err, 'No se pudieron guardar tus datos.')),
  });

  const patchFamMut = useMutation({
    mutationFn: (v: { id: string; patch: Partial<IntegranteDto> }) =>
      patchFamiliar(token, v.id, v.patch),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['onboarding', token] }),
    onError: (err: unknown) =>
      setGlobalError(extractServerError(err, 'No se pudo actualizar.')),
  });

  const finalizarMut = useMutation({
    mutationFn: () => finalizarLegajo(token),
    onSuccess: () => setStep('final'),
    onError: (err: unknown) =>
      setGlobalError(extractServerError(err, 'No se pudo finalizar tu alta.')),
  });
  const tieneObservaciones = finalizarMut.data?.tieneObservacionesPendientes === true;
  const cantObservaciones = finalizarMut.data?.cantidadObservacionesPendientes ?? 0;

  // Si el titular eligió el plan base (o todavía no decidió), el step de pago
  // se omite del stepper — solo el plan opcional requiere pago en este flujo
  // (el base lo cubre la empresa). Cuando aún no decidió, ocultamos el paso
  // para no confundir; si después elige el opcional, lo agregamos dinámicamente.
  const plan =
    legajoQ.data && 'legajo' in legajoQ.data ? legajoQ.data.legajo : null;
  // Empresas sin plan opcional (solo plan base) no tienen nada que elegir:
  // se saltean "Plan" y "Pago" y de la DDJJ se va derecho al cierre. El server
  // manda el flag; si no viene (respuesta vieja), asumimos que sí ofrece.
  const ofrecePlanOpcional = plan?.ofrecePlanOpcional !== false;
  const incluyePago = ofrecePlanOpcional && plan?.planSuperadorElegido === true;
  const visibleSteps = STEPS.filter((s) => {
    if (s.key === 'plan') return ofrecePlanOpcional;
    if (s.key === 'pago') return incluyePago;
    return true;
  });

  const stepIdx = visibleSteps.findIndex((s) => s.key === step);
  const currentStep = visibleSteps[stepIdx] ?? visibleSteps[0];

  const volverAlInicio = () => navigate('/', { replace: true });

  if (legajoQ.isLoading) return <LoadingState />;

  if (legajoQ.isError || !legajoQ.data) {
    return (
      <ErrorState
        message={extractServerError(legajoQ.error, 'No pudimos cargar tu legajo.')}
        onVolver={volverAlInicio}
      />
    );
  }

  // Si el legajo está bloqueado por Medicus (pendiente de validación o
  // rechazado por el admin), mostramos un mensaje específico en lugar del
  // wizard. El titular no puede avanzar hasta que Medicus libere su legajo.
  if ('blockedByMedicus' in legajoQ.data) {
    return (
      <BlockedState
        motivo={legajoQ.data.motivo}
        mensaje={legajoQ.data.mensaje}
        onVolver={volverAlInicio}
      />
    );
  }

  // Si el legajo todavía no pasó la validación OTP, el wizard queda gated:
  // mostramos el flujo de OTP antes que cualquier paso.
  if (legajoQ.data.needsOtp) {
    return (
      <div className="onboarding">
        <header className="onboarding__topbar">
          <MedicusLogo width={110} />
        </header>
        <main className="onboarding__main">
          <OtpGate
            token={token}
            documento={legajoQ.data.documento}
            primerNombre={legajoQ.data.primerNombre}
            metodos={legajoQ.data.metodos}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="onboarding">
      <header className="onboarding__topbar">
        <MedicusLogo width={110} />
        <span className="onboarding__legajoBadge">
          Legajo {legajoQ.data.legajo.numero}
        </span>
      </header>

      <main className="onboarding__main">
        <Stepper steps={visibleSteps} currentIdx={stepIdx} />

        {currentStep.titulo && (
          <section className="onboarding__heading">
            <h1 className="heading-3" style={{ marginBottom: 6 }}>
              {currentStep.titulo}
            </h1>
          </section>
        )}

        {globalError && (
          <p
            className="status status-error"
            style={{ display: 'block', marginBottom: 16 }}
          >
            {globalError}
          </p>
        )}

        {step === 'titular' && (
          <TitularStep
            token={token}
            titular={legajoQ.data.titular}
            observaciones={legajoQ.data.legajo.observaciones ?? []}
            onSubmit={(patch) => {
              setGlobalError(null);
              titularMut.mutate(patch);
            }}
            submitting={titularMut.isPending}
          />
        )}

        {step === 'familia' && (
          <FamiliaStep
            token={token}
            familiares={legajoQ.data.familiares}
            observaciones={legajoQ.data.legajo.observaciones ?? []}
            onBack={() => setStep('titular')}
            onContinue={() => setStep('ddjj')}
            onUpdate={(id, patch) => {
              setGlobalError(null);
              patchFamMut.mutate({ id, patch });
            }}
            busy={patchFamMut.isPending}
          />
        )}

        {step === 'ddjj' && (
          <DdjjStep
            token={token}
            onBack={() => setStep('familia')}
            onContinue={() => {
              // Sin plan opcional no hay nada que elegir ni que cobrar: la DDJJ
              // es el último paso y el alta se finaliza acá mismo.
              if (ofrecePlanOpcional) {
                setStep('plan');
              } else {
                setGlobalError(null);
                finalizarMut.mutate();
              }
            }}
          />
        )}

        {step === 'plan' && (
          <PlanSuperadorStep
            token={token}
            onBack={() => setStep('ddjj')}
            onContinue={(aceptoSuperador) => {
              setGlobalError(null);
              // Usamos directamente la respuesta del server (vía el child)
              // para no depender del query cache, que en este punto recién
              // se invalidó y puede estar stale. Si eligió MS2 → paso de
              // pago. Si eligió MS1 → finaliza el alta directo.
              if (aceptoSuperador) {
                setStep('pago');
              } else {
                finalizarMut.mutate();
              }
            }}
          />
        )}

        {step === 'pago' && (
          <PagoStep
            token={token}
            onBack={() => setStep('plan')}
            onContinue={() => {
              setGlobalError(null);
              finalizarMut.mutate();
            }}
          />
        )}

        {step === 'final' && (
          <FinalCard
            tieneObservaciones={tieneObservaciones}
            cantObservaciones={cantObservaciones}
          />
        )}
      </main>
    </div>
  );
}

function Stepper({
  steps,
  currentIdx,
}: {
  steps: typeof STEPS;
  currentIdx: number;
}) {
  const total = steps.length;
  const actual = Math.max(0, currentIdx) + 1;
  const labelActual = steps[Math.max(0, currentIdx)]?.label ?? '';
  const progressPct = total === 0 ? 0 : Math.round((actual / total) * 100);

  return (
    <>
      {/* Mobile: pill compacto "N/M · Label" + barra de progreso */}
      <div
        className="onboarding__stepperMobile"
        aria-label="Progreso"
        role="progressbar"
        aria-valuenow={actual}
        aria-valuemin={1}
        aria-valuemax={total}
      >
        <span className="onboarding__stepperMobileCount">
          {actual}/{total}
        </span>
        <span style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{labelActual}</span>
        <span className="onboarding__stepperMobileProgress" aria-hidden="true">
          <span
            className="onboarding__stepperMobileProgressBar"
            style={{ width: `${progressPct}%` }}
          />
        </span>
      </div>

      {/* Desktop: stepper completo con cada paso */}
      <div className="onboarding__stepper" aria-label="Progreso">
        {steps.map((s, i) => (
          <div
            key={s.key}
            className={
              i === currentIdx
                ? 'onboarding__stepperItem onboarding__stepperItem--active'
                : i < currentIdx
                  ? 'onboarding__stepperItem onboarding__stepperItem--done'
                  : 'onboarding__stepperItem'
            }
          >
            <span className="onboarding__stepperDot">{i + 1}</span>
            <span className="onboarding__stepperLabel">{s.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}
